import { LVGL_LOG, type IPlatformApi, type LogMessage } from '@lved/shared-core';
import { useMessageStore, type MessageStoreState } from '@lved/ui/store/messageStore';
import { isBrowserOnlyTheia } from '@theia-lved-core/browser/online-preview/environment';
import { TheiaPlatformApiContribution } from '@theia-lved-core/browser/platform/theia-platform-api-contribution';
import { FrontendApplication, FrontendApplicationContribution } from '@theia/core/lib/browser';
import { DisposableCollection } from '@theia/core/lib/common/disposable';
import { inject, injectable } from '@theia/core/shared/inversify';
import { OutputChannel, OutputChannelManager, OutputChannelSeverity } from '@theia/output/lib/browser/output-channel';
import { OutputContribution } from '@theia/output/lib/browser/output-contribution';
import { WorkspaceService } from '@theia/workspace/lib/browser/workspace-service';

const CHANNEL_ALL: string = 'LVGL (All)';
const CHANNEL_LVGL: string = 'LVGL: Renderer';
const CHANNEL_EXPORT: string = 'LVGL: Code Export';
const HIDDEN_CHANNEL_NAMES: string[] = ['hosted-instance-log'];
const BROWSER_ONLY_THEIA: boolean = isBrowserOnlyTheia();

type LogSource = 'lvgl' | 'export' | 'runtime';
type SourceRoutedLogMessage = LogMessage & {
    source?: LogSource;
};
type RoutedLog = {
    source: LogSource;
    message: string;
    status: string;
    options?: LogMessage['options'];
};

/**
 * Selects the LVGL output channel after layout init and mirrors UI/platform logs into it.
 */
@injectable()
export class OutputPanelDefaultContribution implements FrontendApplicationContribution {
    @inject(OutputContribution)
    protected readonly outputContribution!: OutputContribution;

    @inject(OutputChannelManager)
    protected readonly outputChannelManager!: OutputChannelManager;

    @inject(TheiaPlatformApiContribution)
    protected readonly platformApiContribution!: TheiaPlatformApiContribution;

    @inject(WorkspaceService)
    protected readonly workspaceService!: WorkspaceService;

    protected readonly toDispose: DisposableCollection = new DisposableCollection();
    protected readonly lastByChannel: Map<string, string> = new Map();
    protected readonly progressByScope: Map<string, number> = new Map();
    protected previousWorkspaceKey: string | undefined;

    onStart(): void {
        queueMicrotask(() => {
            this.registerHiddenChannelsFilter();
            this.installOutputLogBridge();
        });
        void this.installWorkspaceOutputReset();
    }

    async onDidInitializeLayout(_app: FrontendApplication): Promise<void> {
        if (!BROWSER_ONLY_THEIA) {
            // Eagerly create LVGL channels so they are visible in the Output dropdown.
            this.outputChannelManager.getChannel(CHANNEL_LVGL);
            this.outputChannelManager.getChannel(CHANNEL_EXPORT);
        }
        const allChannel = this.outputChannelManager.getChannel(CHANNEL_ALL);
        this.outputChannelManager.selectedChannel = allChannel;
        await new Promise<void>((resolve) => queueMicrotask(resolve));
        // Open the view, then `show()` the channel (as the dropdown does) so the editor binds to
        // it — setting `selectedChannel` alone updates the label but leaves it blank until
        // reselected. `preserveFocus: true` reveals without stealing keyboard focus on startup.
        await this.outputContribution.openView({ activate: false, reveal: true });
        allChannel.show({ preserveFocus: true });
    }

    protected registerHiddenChannelsFilter(): void {
        type ManagedOutputChannel = {
            name?: string;
            hide?: () => void;
        };
        type ManagedOutputChannelManager = {
            getChannels?: () => Iterable<ManagedOutputChannel>;
            onChannelAdded?: (listener: (channel: ManagedOutputChannel) => void) => { dispose: () => void } | void;
        };
        const manager: ManagedOutputChannelManager = this
            .outputChannelManager as unknown as ManagedOutputChannelManager;

        const hideChannel = (channel: ManagedOutputChannel): void => {
            const channelName: string = (channel.name ?? '').trim();
            const shouldHide: boolean =
                HIDDEN_CHANNEL_NAMES.includes(channelName) || (BROWSER_ONLY_THEIA && channelName !== CHANNEL_ALL);
            if (!shouldHide) return;
            channel.hide?.();
        };

        for (const channel of Array.from(manager.getChannels?.() ?? [])) hideChannel(channel);

        const onAddedDisposable = manager.onChannelAdded?.((channel: ManagedOutputChannel) => {
            hideChannel(channel);
        });
        if (onAddedDisposable?.dispose) this.toDispose.push(onAddedDisposable);
    }

    protected async installWorkspaceOutputReset(): Promise<void> {
        await this.workspaceService.ready;
        this.previousWorkspaceKey = this.getWorkspaceKey();
        this.toDispose.push(
            this.workspaceService.onWorkspaceChanged((): void => {
                const nextWorkspaceKey: string | undefined = this.getWorkspaceKey();
                const shouldClear: boolean =
                    this.previousWorkspaceKey !== undefined && nextWorkspaceKey !== this.previousWorkspaceKey;

                this.previousWorkspaceKey = nextWorkspaceKey;
                if (shouldClear) this.clearWorkspaceOutput();
            })
        );
    }

    protected getWorkspaceKey(): string | undefined {
        const roots: string[] = this.workspaceService
            .tryGetRoots()
            .map((root) => root.resource.toString())
            .sort();
        return roots.length > 0 ? roots.join('|') : undefined;
    }

    protected clearWorkspaceOutput(): void {
        const channels: Set<OutputChannel> = new Set<OutputChannel>(this.outputChannelManager.getChannels());
        const routedChannelNames: string[] = BROWSER_ONLY_THEIA
            ? [CHANNEL_ALL]
            : [CHANNEL_ALL, CHANNEL_LVGL, CHANNEL_EXPORT];
        for (const channelName of routedChannelNames) {
            channels.add(this.outputChannelManager.getChannel(channelName));
        }

        for (const channel of channels) channel.clear();

        this.lastByChannel.clear();
        this.progressByScope.clear();
    }

    protected async installOutputLogBridge(): Promise<void> {
        const unsubscribeMessageStore: () => void = useMessageStore.subscribe((state: MessageStoreState) => {
            this.routeLogMessage({
                status: 'log',
                message: state.message,
                options: state.options
            });
        });
        this.toDispose.push({
            dispose: (): void => {
                unsubscribeMessageStore();
            }
        });

        const handleLvglLog = (event: Event): void => {
            const detail: unknown = (event as CustomEvent<unknown>).detail;
            this.routeLogMessage(OutputPanelDefaultContribution.toLogMessage(detail));
        };
        window.addEventListener(LVGL_LOG, handleLvglLog);
        this.toDispose.push({
            dispose: (): void => {
                window.removeEventListener(LVGL_LOG, handleLvglLog);
            }
        });

        const api: IPlatformApi = await this.platformApiContribution.apiReady;
        const callbackId: symbol = api.onLogMessage((_event: unknown, message: LogMessage): void => {
            this.routeLogMessage(message);
        });
        this.toDispose.push({
            dispose: (): void => {
                api.removeOnLogMessageListener(callbackId);
            }
        });
    }

    protected routeLogMessage(message: SourceRoutedLogMessage): void {
        const text: string = OutputPanelDefaultContribution.normalizeMessage(message.message);
        if (!text) return;
        const source: LogSource = message.source ?? OutputPanelDefaultContribution.classifySource(text);
        const prefixedMessage: string =
            source === 'lvgl' ? OutputPanelDefaultContribution.ensureLvglPrefix(text) : text;
        const compacted: string | undefined = this.compactNoisyMessage(source, prefixedMessage);
        if (!compacted) return;
        const routed: RoutedLog = { source, message: compacted, status: message.status, options: message.options };
        this.appendRoutedLog(routed);
    }

    protected appendRoutedLog(log: RoutedLog): void {
        this.appendToChannel(CHANNEL_ALL, log);
        if (BROWSER_ONLY_THEIA) return;

        const specificChannelName: string = this.channelForSource(log.source);
        this.appendToChannel(specificChannelName, log);
    }

    protected appendToChannel(channelName: string, log: RoutedLog): void {
        if (this.lastByChannel.get(channelName) === log.message) return;
        this.lastByChannel.set(channelName, log.message);
        const channel: OutputChannel = this.outputChannelManager.getChannel(channelName);
        const severity: OutputChannelSeverity = OutputPanelDefaultContribution.logStatusToOutputSeverity(
            log.status,
            log.message
        );
        if (log.options?.noNewline) channel.append(log.message, severity);
        else channel.appendLine(log.message, severity);
    }

    protected compactNoisyMessage(source: LogSource, message: string): string | undefined {
        if (source !== 'runtime') return message;

        const progressPatterns: Array<{ scope: string; regex: RegExp }> = [
            { scope: 'download', regex: /(?:Downloading|Download complete)[^(]*\((\d+)%\)/i }
        ];
        for (const pattern of progressPatterns) {
            const match: RegExpMatchArray | null = message.match(pattern.regex);
            if (!match) continue;
            const percentage: number = Number.parseInt(match[1], 10);
            if (Number.isNaN(percentage)) return undefined;
            const previous: number = this.progressByScope.get(pattern.scope) ?? -1;
            const progressRestarted: boolean = previous >= 0 && percentage < previous;
            const shouldEmit: boolean =
                percentage === 100 || percentage - previous >= 5 || previous < 0 || progressRestarted;
            if (!shouldEmit) return undefined;
            this.progressByScope.set(pattern.scope, percentage);
            return `Downloading (${percentage}%)`;
        }

        return message;
    }

    protected static toLogMessage(detail: unknown): SourceRoutedLogMessage {
        if (typeof detail === 'string') return { status: 'log', message: detail };

        if (!detail || typeof detail !== 'object') return { status: 'log', message: String(detail) };

        const record: Record<string, unknown> = detail as Record<string, unknown>;
        const source: LogSource | undefined = OutputPanelDefaultContribution.isLogSource(record.source)
            ? record.source
            : undefined;
        return {
            status: typeof record.status === 'string' ? record.status : 'log',
            message: typeof record.message === 'string' ? record.message : String(record.message ?? ''),
            options: record.options as LogMessage['options'],
            source
        };
    }

    protected static isLogSource(value: unknown): value is LogSource {
        return ['lvgl', 'export', 'runtime'].includes(value as string);
    }

    protected channelForSource(source: LogSource): string {
        switch (source) {
            case 'lvgl':
                return CHANNEL_LVGL;
            case 'export':
                return CHANNEL_EXPORT;
            case 'runtime':
                return CHANNEL_LVGL;
        }
    }

    protected static classifySource(message: string): LogSource {
        if (message.startsWith('[LVGL]')) return 'lvgl';
        if (
            /(code generated|generating code|generate code|code generation|code export|generate component|generate widget)/i.test(
                message
            )
        ) {
            return 'export';
        }
        return 'runtime';
    }

    protected static ensureLvglPrefix(message: string): string {
        return message.startsWith('[LVGL]') ? message : `[LVGL] ${message}`;
    }

    protected static normalizeMessage(message: string): string {
        // The Output panel is a plain Monaco text view and cannot interpret ANSI
        // escapes (compilers/cmake emit colored diagnostics), so they would show
        // up as literal `␛[..m` codes. Strip them before appending.
        // eslint-disable-next-line no-control-regex
        const ansiEscape: RegExp = /\x1B(?:[@-Z\\-_]|\[[0-?]*[ -/]*[@-~])/g;
        return message
            .replace(ansiEscape, '')
            .replace(/(?:\r\n|\r|\n)+$/, '')
            .trim();
    }

    protected static logStatusToOutputSeverity(status: string, message: string): OutputChannelSeverity {
        // The Output panel only supports two colored severities (Error -> red,
        // Warning -> yellow); Info is the default text color. Drive the severity
        // from the explicit status as well as keywords in the message text so
        // build output (compiler/make) gets colored even without a status.
        const normalizedStatus: string = status.toLowerCase();
        const normalizedMessage: string = message.toLowerCase();
        if (
            normalizedStatus.includes('error') ||
            message.includes('❌') ||
            /\b(?:error|errors|failed|failure|fatal)\b/.test(normalizedMessage)
        ) {
            return OutputChannelSeverity.Error;
        }
        if (normalizedStatus.includes('warn') || /\b(?:warning|warn)\b/.test(normalizedMessage)) {
            return OutputChannelSeverity.Warning;
        }
        return OutputChannelSeverity.Info;
    }
}
